import React, { useRef, ComponentRef } from 'react';
import { Col, Form, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Button, DatePicker, Input } from 'components';
import { SchemeAutocomplete } from 'modules';
import BeneficiariesList from '../BeneficiariesList';
import CommissionFormGroup from '../CommissionFormGroup';

import useForm from 'hooks/forms/useForm';
import useValidation from 'hooks/forms/useValidation';
import useAutoFocus from 'hooks/elements/useAutoFocus';
import getSchemeFormParams from 'utils/partnerScheme/getSchemeFormParams';
import useBeneficiariesCommissions from './hooks/useBeneficiariesCommissions';

import { INewSchemeFormParams } from '../../types/form';
import { INewSchemeFormProps } from './NewSchemeForm.types';
import { PartnerSchemeType } from 'types/partnerSchemes';

import { DEFAULT_FORM_VALUES, MIN_NAME_LEN } from './constants/defaultValues';
import { DATE_SLASH_FORMAT_WITH_TIME_PICKER, TIME_FORMAT } from 'constants/date';

import './NewSchemeForm.styles.scss';

const { Text } = Typography;

const NewSchemeForm = ({ loading, isEditMode, initialValues, onFinish }: INewSchemeFormProps) => {
  const rules = useValidation();
  const { t } = useTranslation();
  const [form] = Form.useForm<INewSchemeFormParams>();

  const { submitDisabled } = useForm(form);

  const isDefault = initialValues?.isdefault;

  const pps = Form.useWatch(['pps'], form);
  const pplns = Form.useWatch(['pplns'], form);
  const beneficiaries = Form.useWatch(['beneficiaries'], form);

  const schemeRef = useRef<ComponentRef<typeof SchemeAutocomplete> | null>(null);

  useAutoFocus(schemeRef);

  const onSelectScheme = (scheme: PartnerSchemeType) => {
    form.resetFields();

    const formScheme = getSchemeFormParams(scheme);

    form.setFieldsValue(formScheme);
  };

  const handleFinish = (data: INewSchemeFormParams) => {
    onFinish(isEditMode ? { ...data, id: initialValues.id } : data);
  };

  useBeneficiariesCommissions({ form, beneficiaries });

  return (
    <Form
      form={form}
      layout="vertical"
      name="np-new-scheme-form"
      initialValues={initialValues || DEFAULT_FORM_VALUES}
      onFinish={handleFinish}
      requiredMark={false}
      scrollToFirstError
      className="np-new-scheme-form"
    >
      {(!isDefault || !isEditMode) && (
        <div className="np-new-scheme-form__scheme">
          <Text>{t('form.label.inheritFromScheme')}</Text>
          <SchemeAutocomplete value={initialValues?.name} onSelect={onSelectScheme} ref={schemeRef} />
        </div>
      )}

      <Form.Item
        name="name"
        label={t('form.label.newSchemeName')}
        rules={[rules.required, rules.requiredMinLen(MIN_NAME_LEN)]}
      >
        <Input disabled={loading} />
      </Form.Item>

      {!isDefault && (
        <Form.Item label={t('form.label.expirationDate')} name="expDate">
          <DatePicker
            block
            hideFooter
            showNow={false}
            disabled={loading}
            format={DATE_SLASH_FORMAT_WITH_TIME_PICKER}
            showTime={{ defaultValue: dayjs('00:00', TIME_FORMAT) }}
          />
        </Form.Item>
      )}

      <BeneficiariesList value={beneficiaries} />

      <Row gutter={[16, 16]} className="np-beneficiaries-list__item">
        <Col flex="1 1 100%">
          <CommissionFormGroup value={pps} fieldsPrefix="pps" title="form.label.ppsCommission" loading={loading} />
        </Col>

        <Col flex="1 1 100%">
          <CommissionFormGroup
            value={pplns}
            fieldsPrefix="pplns"
            title="form.label.pplnsCommission"
            loading={loading}
          />
        </Col>
      </Row>

      <Form.Item>
        <Button htmlType="submit" block loading={loading} disabled={submitDisabled}>
          {t('form.button.submit')}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default NewSchemeForm;
